import { useState } from 'react';
import { Container } from 'react-bootstrap';
import Button from '../../../components/Shared/Button';
import Loader from '../../../components/Shared/Loader';
import PermissionsGate from '../../../components/PermissionsGate';
import { SCOPES } from '../../../helpers/permission-maps';
import PRMTable from '../Components/PRMTable/PRMTable';
import PRMToolBar from '../Components/PRMToolBar/PRMToolBar';
import PRMSearch from '../Components/PRMSearch/PRMSearch';
import AccountsFilter from './filters/AccountsFilter';
import type { AccountsFilterValues } from './filters/AccountsFilterTypes';
import { filterAccounts } from './filters/accountFilters';
import useAccountData from './hooks/AccountData';
import './accounts.scss';
import { columns, accountActions } from './accountsTableConfig';

const AccountsListing = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AccountsFilterValues>({});
  const [searchTerm, setSearchTerm] = useState('');
  const { accounts, isLoading, error } = useAccountData();
  const filteredAccounts = filterAccounts(accounts, appliedFilters, searchTerm);

  return (
    <>
      <div className="accounts-page">
        <Container fluid>
          <h2 className="Heading">Accounts</h2>
          {isLoading && (
            <output className="d-block" aria-label="Loading accounts">
              <Loader />
            </output>
          )}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {!isLoading && !error && (
            <PRMTable
              paginationResetKey={JSON.stringify([appliedFilters, searchTerm])}
              rows={{ data: filteredAccounts, actions: accountActions }}
              columns={columns}
              toolbar={
                <div className="accounts-toolbar">
                  <PRMToolBar
                    toolBarStart={
                      <>
                        <div className="accounts-search">
                          <PRMSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            label="Search company by name"
                            placeholder="Search for Company Name"
                          />
                        </div>
                        <fieldset className="accounts-filterControl" aria-label="Account filters">
                          <Button
                            className="Button Button--whiteBlack accounts-filterOpen"
                            aria-label="More Filters"
                            data-testid="moreFilters"
                            onClick={() => setShowFilters(true)}
                          >
                            Filters
                          </Button>
                          <button
                            type="button"
                            className="accounts-filterReset"
                            aria-label="Reset Filters"
                            title="Reset Filters"
                            onClick={() => setAppliedFilters({})}
                          >
                            <i className="icon-Close" aria-hidden="true"></i>
                          </button>
                        </fieldset>
                      </>
                    }
                    toolBarEnd={
                      <Button className="Button Button--whiteBlack accounts-newAccount" data-testid="addNewAccount">
                        New Account <i className="icon-Plus" aria-hidden="true"></i>
                      </Button>
                    }
                  />
                </div>
              }
            />
          )}
        </Container>
      </div>
      <AccountsFilter
        records={accounts}
        appliedFilters={appliedFilters}
        show={showFilters}
        onClose={() => setShowFilters(false)}
        onSubmit={setAppliedFilters}
        onClear={() => setAppliedFilters({})}
      />
    </>
  );
};

const Accounts = () => (
  <PermissionsGate scopes={[SCOPES.canAccessPrmAccounts]} redirect>
    <AccountsListing />
  </PermissionsGate>
);

export default Accounts;
